import React, { useEffect, useState } from 'react';
import { Clock, ChevronLeft, ChevronRight, X, ArrowUpDown, ArrowLeft, Check, Pencil, Trash2, Info } from 'lucide-react';
interface ActivityEntry {
  action: string;
  user: string;
  timestamp: string;
}
interface Webhook {
  id: string;
  name: string;
  url: string;
  type: string;
  status: 'Active' | 'Inactive';
  events: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  activity: ActivityEntry[];
}
const AVAILABLE_EVENTS = ['Client tokenisation status', 'Transaction change', 'Tokenisation status', 'Payment status', 'Refund status', 'Dispute status', 'Settlement status'];
function formatDate(iso: string) {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  const secs = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}:${secs}`;
}
/* ── Centralised style tokens (mapped from theme/tokens.css) ── */
const styles = {
  // Buttons
  btnPrimary: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]',
  btnDanger: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--colors-error-default)] hover:bg-[var(--colors-error-dark)] focus:outline-none focus:ring-2 focus:ring-red-300',
  btnGhost: 'text-sm text-[var(--colors-text-secondary)] bg-[var(--colors-background-card)] border border-[var(--colors-border-default)] rounded-md hover:bg-[var(--colors-neutral-50)] transition-colors',
  // Cards
  card: 'bg-[var(--colors-background-card)] border border-[var(--colors-border-light)] rounded-lg shadow-[var(--shadows-sm)]',
  // Text
  heading: 'text-[var(--colors-text-primary)] font-semibold',
  subtext: 'text-sm text-[var(--colors-text-tertiary)]',
  label: 'block text-sm font-medium text-[var(--colors-text-secondary)] mb-1',
  // Inputs
  input: 'w-full border border-[var(--colors-border-default)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--colors-border-focus)] bg-[var(--colors-background-card)]',
  // Badges
  badgeActive: 'bg-[var(--colors-success-light)] text-[var(--colors-success-dark)]',
  badgeInactive: 'bg-[var(--colors-neutral-100)] text-[var(--colors-neutral-600)]',
  badgeTypeGlobal: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)]',
  badgeTypeOther: 'bg-[var(--colors-primary-100)] text-[var(--colors-primary-800)]',
  badgeBase: 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium',
  // Table
  th: 'px-6 py-3 font-medium text-[var(--colors-text-secondary)] cursor-pointer hover:bg-[var(--colors-neutral-50)]',
  // Misc
  eventChip: 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-[var(--colors-neutral-100)] text-[var(--colors-text-secondary)] border border-[var(--colors-border-light)]',
  overlay: 'fixed inset-0 bg-[var(--colors-background-overlay)] flex items-center justify-center z-[var(--zindex-modal)]'
} as const;
type ViewMode = 'list' | 'add' | 'edit';
export function WebhookEndpoints() {
  const [globalWebhookUrl, setGlobalWebhookUrl] = useState('');
  const [globalUpdateStatus, setGlobalUpdateStatus] = useState<'idle' | 'success'>('idle');
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null);
  const [deleteModalWebhook, setDeleteModalWebhook] = useState<Webhook | null>(null);
  const [infoModalWebhook, setInfoModalWebhook] = useState<Webhook | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'Global',
    events: [] as string[]
  });
  useEffect(() => {
    if (globalUpdateStatus === 'success') {
      const timer = setTimeout(() => setGlobalUpdateStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
  }, [globalUpdateStatus]);
  const handleGlobalUpdate = () => {
    setGlobalUpdateStatus('success');
  };
  const handleGlobalRemove = () => {
    if (window.confirm('Are you sure you want to remove the global webhook?')) {
      setGlobalWebhookUrl('');
    }
  };
  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      type: 'Global',
      events: []
    });
  };
  const openAddView = () => {
    resetForm();
    setViewMode('add');
  };
  const openEditView = (webhook: Webhook) => {
    setFormData({
      name: webhook.name,
      url: webhook.url,
      type: webhook.type,
      events: [...webhook.events]
    });
    setEditingWebhookId(webhook.id);
    setViewMode('edit');
  };
  const handleAddWebhook = () => {
    if (!formData.name || !formData.url || formData.events.length === 0) return;
    const now = new Date().toISOString();
    const newWebhook: Webhook = {
      id: Math.random().toString(36).substring(2, 18),
      name: formData.name,
      url: formData.url,
      type: formData.type,
      status: 'Active',
      events: formData.events,
      createdAt: now,
      updatedAt: now,
      createdBy: 'admin@cimb.com.my',
      activity: [{
        action: 'Webhook created',
        user: 'admin@cimb.com.my',
        timestamp: now
      }]
    };
    setWebhooks([...webhooks, newWebhook]);
    resetForm();
    setViewMode('list');
  };
  const handleSaveEdit = () => {
    if (!formData.name || !formData.url || formData.events.length === 0) return;
    const now = new Date().toISOString();
    setWebhooks(webhooks.map(wh => wh.id === editingWebhookId ? {
      ...wh,
      name: formData.name,
      url: formData.url,
      type: formData.type,
      events: formData.events,
      updatedAt: now,
      activity: [...wh.activity, {
        action: 'Webhook updated',
        user: 'admin@cimb.com.my',
        timestamp: now
      }]
    } : wh));
    setEditingWebhookId(null);
    resetForm();
    setViewMode('list');
  };
  const handleDeleteWebhook = (webhook: Webhook) => {
    setWebhooks(webhooks.filter(wh => wh.id !== webhook.id));
    setDeleteModalWebhook(null);
  };
  const toggleEvent = (event: string) => {
    if (formData.events.includes(event)) {
      setFormData({
        ...formData,
        events: formData.events.filter(e => e !== event)
      });
    } else {
      setFormData({
        ...formData,
        events: [...formData.events, event]
      });
    }
  };
  const selectAllEvents = () => {
    setFormData({
      ...formData,
      events: [...AVAILABLE_EVENTS]
    });
  };
  const clearAllEvents = () => {
    setFormData({
      ...formData,
      events: []
    });
  };
  const statusBadge = (status: string) => status === 'Active' ? styles.badgeActive : styles.badgeInactive;
  const typeBadge = (type: string) => type === 'Global' ? styles.badgeTypeGlobal : styles.badgeTypeOther;
  // ─── Add / Edit View ───
  if (viewMode === 'add' || viewMode === 'edit') {
    const isEdit = viewMode === 'edit';
    return <div className="max-w-5xl mx-auto p-6 space-y-6" data-id="element-5314">
        <div className={`${styles.card} p-4 flex items-center gap-3`} data-id="element-5315">
          <button onClick={() => {
          setViewMode('list');
          resetForm();
          setEditingWebhookId(null);
        }} className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-primary)] transition-colors rounded-md hover:bg-[var(--colors-neutral-100)]" data-id="element-5316">
            <ArrowLeft className="w-5 h-5" data-id="element-5317" />
          </button>
          <h1 className={`text-lg font-medium ${styles.heading}`} data-id="element-5318">
            {isEdit ? 'Edit Webhook' : 'Add Webhook'}
          </h1>
        </div>

        <div className={styles.card} data-id="element-5319">
          <div className="p-6 space-y-6" data-id="element-5320">
            <div data-id="element-5321">
              <label className={styles.label} data-id="element-5322">
                Name{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5323">*</span>
              </label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className={styles.input} placeholder="e.g, Payment Notifications" data-id="element-5324" />
            </div>

            <div data-id="element-5325">
              <label className={styles.label} data-id="element-5326">
                Webhook URL{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5327">*</span>
              </label>
              <input type="url" value={formData.url} onChange={e => setFormData({
              ...formData,
              url: e.target.value
            })} className={styles.input} placeholder="https://example.com/webhook" data-id="element-5328" />
            </div>

            <div data-id="element-5329">
              <label className={styles.label} data-id="element-5330">
                Type{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5331">*</span>
              </label>
              <select value={formData.type} onChange={e => setFormData({
              ...formData,
              type: e.target.value
            })} className={styles.input} data-id="element-5332">
                <option value="Global" data-id="element-5333">Global</option>
                <option value="Transaction-level" data-id="element-5334">Transaction-level</option>
                <option value="Payment" data-id="element-5335">Payment</option>
                <option value="Refund" data-id="element-5336">Refund</option>
                <option value="Dispute" data-id="element-5337">Dispute</option>
                <option value="All" data-id="element-5338">All</option>
              </select>
            </div>

            <div data-id="element-5339">
              <label className={styles.label} data-id="element-5340">
                Events{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5341">*</span>
              </label>
              <div className="border border-[var(--colors-border-default)] rounded-md p-1.5 min-h-[42px] flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-[var(--brand-primaryring)] focus-within:border-[var(--colors-border-focus)] relative bg-[var(--colors-background-card)] pr-8" data-id="element-5342">
                {formData.events.map(event => <span key={event} className={`${styles.eventChip} gap-1`} data-id="element-5343">
                    {event}
                    <button onClick={() => toggleEvent(event)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] focus:outline-none" data-id="element-5344">
                      <X className="w-3.5 h-3.5" data-id="element-5345" />
                    </button>
                  </span>)}
                <select className="flex-1 min-w-[150px] border-none focus:ring-0 text-sm py-1 bg-transparent text-[var(--colors-text-tertiary)] outline-none" onChange={e => {
                if (e.target.value) {
                  toggleEvent(e.target.value);
                  e.target.value = '';
                }
              }} value="" data-id="element-5346">
                  <option value="" disabled data-id="element-5347">
                    -- Select one --
                  </option>
                  {AVAILABLE_EVENTS.filter(e => !formData.events.includes(e)).map(event => <option key={event} value={event} data-id="element-5348">
                      {event}
                    </option>)}
                </select>
                {formData.events.length > 0 && <button onClick={clearAllEvents} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-5349">
                    <X className="w-4 h-4" data-id="element-5350" />
                  </button>}
              </div>
              <div className="mt-2" data-id="element-5351">
                <button onClick={selectAllEvents} className={`${styles.btnGhost} px-3 py-1.5`} data-id="element-5352">
                  Select all
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[var(--colors-border-light)] flex justify-end" data-id="element-5353">
            <button onClick={isEdit ? handleSaveEdit : handleAddWebhook} disabled={!formData.name || !formData.url || formData.events.length === 0} className={`px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${isEdit ? styles.btnDanger : styles.btnPrimary}`} data-id="element-5354">
              {isEdit ? 'Save changes' : 'Add webhook'}
            </button>
          </div>
        </div>
      </div>;
  }
  // ─── List View ───
  return <div className="max-w-7xl mx-auto p-6 space-y-6" data-id="element-5355">
      {/* Header */}
      <div className={`${styles.card} p-6 flex justify-between items-center`} data-id="element-5356">
        <div data-id="element-5357">
          <h1 className={`text-xl ${styles.heading}`} data-id="element-5358">Webhooks</h1>
          <p className={`${styles.subtext} mt-1`} data-id="element-5359">
            Manage webhook endpoints for receiving real-time event notifications
          </p>
        </div>
        <button onClick={openAddView} className={`${styles.btnPrimary} px-4 py-2`} data-id="element-5360">
          Add Webhook
        </button>
      </div>

      {/* Global Webhook */}
      <div className={styles.card} data-id="element-5361">
        <div className="p-6 border-b border-[var(--colors-border-light)]" data-id="element-5362">
          <h2 className={`text-base font-medium ${styles.heading}`} data-id="element-5363">
            Global Webhook
          </h2>
          <p className={`${styles.subtext} mt-1`} data-id="element-5364">
            Configure a global webhook URL to receive events for all
            transactions.
          </p>
          <div className="mt-6" data-id="element-5365">
            <label className={styles.label} data-id="element-5366">Webhook URL</label>
            <input type="text" value={globalWebhookUrl} onChange={e => setGlobalWebhookUrl(e.target.value)} placeholder="https://example.com/webhook" className={styles.input} data-id="element-5367" />
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end gap-3 rounded-b-lg" data-id="element-5368">
          <button onClick={handleGlobalUpdate} className={`${styles.btnPrimary} px-4 py-2 min-w-[80px] flex justify-center items-center`} data-id="element-5369">
            {globalUpdateStatus === 'success' ? <span className="flex items-center gap-1" data-id="element-5370">
                <Check className="w-4 h-4" data-id="element-5371" /> Updated!
              </span> : 'Update'}
          </button>
          <button onClick={handleGlobalRemove} className={`${styles.btnDanger} px-4 py-2`} data-id="element-5372">
            Remove
          </button>
        </div>
      </div>

      {/* Configured Webhooks */}
      <div className={styles.card} data-id="element-5373">
        <div className="p-6 border-b border-[var(--colors-border-light)]" data-id="element-5374">
          <h2 className={`text-base font-medium ${styles.heading}`} data-id="element-5375">
            Configured Webhooks
          </h2>
        </div>

        <div className="overflow-x-auto" data-id="element-5376">
          <table className="w-full text-left text-sm text-[var(--colors-text-secondary)]" data-id="element-5377">
            <thead className="border-b border-[var(--colors-border-light)]" data-id="element-5378">
              <tr data-id="element-5379">
                <th className={styles.th} data-id="element-5380">
                  <div className="flex items-center gap-2" data-id="element-5381">
                    Name & URL{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5382" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-5383">
                  <div className="flex items-center gap-2" data-id="element-5384">
                    Type{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5385" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-5386">
                  <div className="flex items-center gap-2" data-id="element-5387">
                    Status{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5388" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-5389">
                  <div className="flex items-center gap-2" data-id="element-5390">
                    Events{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5391" />
                  </div>
                </th>
                <th className="px-6 py-3 font-medium text-[var(--colors-text-secondary)] text-right" data-id="element-5392">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5393">
              {webhooks.length === 0 ? <tr data-id="element-5394">
                  <td colSpan={5} className="px-6 py-24 text-center" data-id="element-5395">
                    <div className="flex flex-col items-center justify-center" data-id="element-5396">
                      <div className="w-12 h-12 bg-[var(--colors-neutral-50)] rounded-full flex items-center justify-center mb-4" data-id="element-5397">
                        <Clock className="w-6 h-6 text-[var(--colors-text-tertiary)]" data-id="element-5398" />
                      </div>
                      <h3 className={`text-base font-medium ${styles.heading} mb-1`} data-id="element-5399">
                        No webhooks
                      </h3>
                      <p className={styles.subtext} data-id="element-5400">
                        Webhooks you add will appear here.
                      </p>
                    </div>
                  </td>
                </tr> : webhooks.map(webhook => <tr key={webhook.id} className="border-b border-[var(--colors-neutral-100)] last:border-0 hover:bg-[var(--colors-neutral-50)]" data-id="element-5401">
                    <td className="px-6 py-4" data-id="element-5402">
                      <div className="font-medium text-[var(--colors-text-primary)]" data-id="element-5403">
                        {webhook.name || '—'}
                      </div>
                      <div className="text-[var(--colors-text-tertiary)] text-xs mt-0.5 font-mono" data-id="element-5404">
                        {webhook.url}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-5405">
                      <span className={`${styles.badgeBase} ${typeBadge(webhook.type)}`} data-id="element-5406">
                        {webhook.type}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-5407">
                      <span className={`${styles.badgeBase} ${statusBadge(webhook.status)}`} data-id="element-5408">
                        {webhook.status}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-5409">
                      <div className="flex flex-wrap gap-1" data-id="element-5410">
                        {webhook.events.map(event => <span key={event} className={styles.eventChip} data-id="element-5411">
                            {event}
                          </span>)}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-5412">
                      <div className="flex items-center justify-end gap-1" data-id="element-5413">
                        <button onClick={() => openEditView(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] transition-colors" title="Edit" data-id="element-5414">
                          <Pencil className="w-4 h-4" data-id="element-5415" />
                        </button>
                        <button onClick={() => setDeleteModalWebhook(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-error-light)] text-[var(--colors-error-default)] hover:text-[var(--colors-error-dark)] transition-colors" title="Delete" data-id="element-5416">
                          <Trash2 className="w-4 h-4" data-id="element-5417" />
                        </button>
                        <button onClick={() => setInfoModalWebhook(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-info-light)] text-[var(--colors-info-default)] hover:text-[var(--colors-info-dark)] transition-colors" title="Details" data-id="element-5418">
                          <Info className="w-4 h-4" data-id="element-5419" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-[var(--colors-border-light)] bg-[var(--colors-neutral-50)] flex items-center justify-between rounded-b-lg" data-id="element-5420">
          <div className="text-sm text-[var(--colors-text-tertiary)] font-medium" data-id="element-5421">
            {webhooks.length === 0 ? 'No entries' : `Showing 1-${webhooks.length} of ${webhooks.length} ${webhooks.length === 1 ? 'entry' : 'entries'}`}
          </div>
          <div className="flex items-center gap-4" data-id="element-5422">
            <div className="flex items-center gap-2" data-id="element-5423">
              <span className="text-sm text-[var(--colors-text-tertiary)] font-medium" data-id="element-5424">
                Show
              </span>
              <select className="border border-[var(--colors-border-default)] rounded-md text-sm py-1.5 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] bg-[var(--colors-background-card)]" data-id="element-5425">
                <option data-id="element-5426">25</option>
                <option data-id="element-5427">50</option>
                <option data-id="element-5428">100</option>
              </select>
            </div>
            <div className="flex items-center gap-1" data-id="element-5429">
              <button className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] disabled:opacity-50" disabled data-id="element-5430">
                <ChevronLeft className="w-4 h-4" data-id="element-5431" />
              </button>
              {webhooks.length > 0 && <span className="text-sm font-medium text-[var(--colors-text-secondary)] px-2" data-id="element-5432">
                  1
                </span>}
              <button className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] disabled:opacity-50" disabled data-id="element-5433">
                <ChevronRight className="w-4 h-4" data-id="element-5434" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalWebhook && <div className={styles.overlay} data-id="element-5435">
          <div className="bg-[var(--colors-background-card)] rounded-lg shadow-[var(--shadows-xl)] w-full max-w-md overflow-hidden" data-id="element-5436">
            <div className="flex justify-end p-4 pb-0" data-id="element-5437">
              <button onClick={() => setDeleteModalWebhook(null)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-5438">
                <X className="w-5 h-5" data-id="element-5439" />
              </button>
            </div>
            <div className="px-6 pb-6 pt-2 text-center" data-id="element-5440">
              <h3 className={`text-xl ${styles.heading} mb-2`} data-id="element-5441">
                Delete Webhook
              </h3>
              <p className={`${styles.subtext} leading-relaxed`} data-id="element-5442">
                Are you sure you want to delete Webhook?
                <br data-id="element-5443" />
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-center gap-4" data-id="element-5444">
              <button onClick={() => setDeleteModalWebhook(null)} className="text-sm font-medium text-[var(--colors-text-secondary)] hover:text-[var(--colors-text-primary)] transition-colors" data-id="element-5445">
                No, go back
              </button>
              <button onClick={() => handleDeleteWebhook(deleteModalWebhook)} className={`${styles.btnDanger} px-4 py-2`} data-id="element-5446">
                Yes, delete Webhook
              </button>
            </div>
          </div>
        </div>}

      {/* Info / Detail Modal */}
      {infoModalWebhook && <div className={styles.overlay} data-id="element-5447">
          <div className="bg-[var(--colors-background-card)] rounded-lg shadow-[var(--shadows-xl)] w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" data-id="element-5448">
            <div className="flex justify-end p-4 pb-0 flex-shrink-0" data-id="element-5449">
              <button onClick={() => setInfoModalWebhook(null)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-5450">
                <X className="w-5 h-5" data-id="element-5451" />
              </button>
            </div>
            <div className="px-6 pb-6 overflow-y-auto" data-id="element-5452">
              <table className="w-full text-sm" data-id="element-5453">
                <tbody className="divide-y divide-[var(--colors-neutral-100)]" data-id="element-5454">
                  <tr data-id="element-5455">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top w-36" data-id="element-5456">
                      Webhook ID
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)] font-mono text-xs" data-id="element-5457">
                      {infoModalWebhook.id}
                    </td>
                  </tr>
                  <tr data-id="element-5458">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5459">
                      Name
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-5460">
                      {infoModalWebhook.name || '—'}
                    </td>
                  </tr>
                  <tr data-id="element-5461">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5462">
                      Endpoint URL
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)] break-all" data-id="element-5463">
                      {infoModalWebhook.url}
                    </td>
                  </tr>
                  <tr data-id="element-5464">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5465">
                      Type
                    </td>
                    <td className="py-3" data-id="element-5466">
                      <span className={`${styles.badgeBase} ${typeBadge(infoModalWebhook.type)}`} data-id="element-5467">
                        {infoModalWebhook.type}
                      </span>
                    </td>
                  </tr>
                  <tr data-id="element-5468">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5469">
                      Status
                    </td>
                    <td className="py-3" data-id="element-5470">
                      <span className={`${styles.badgeBase} ${statusBadge(infoModalWebhook.status)}`} data-id="element-5471">
                        {infoModalWebhook.status}
                      </span>
                    </td>
                  </tr>
                  <tr data-id="element-5472">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5473">
                      Events
                    </td>
                    <td className="py-3" data-id="element-5474">
                      <p className="text-[var(--colors-text-tertiary)] text-xs mb-2" data-id="element-5475">
                        This webhook receives all event types.
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-5476">
                        {infoModalWebhook.events.map(event => <span key={event} className={styles.eventChip} data-id="element-5477">
                            {event}
                          </span>)}
                      </div>
                    </td>
                  </tr>
                  <tr data-id="element-5478">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5479">
                      Created
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-5480">
                      {formatDate(infoModalWebhook.createdAt)}
                    </td>
                  </tr>
                  <tr data-id="element-5481">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5482">
                      Last updated
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-5483">
                      {formatDate(infoModalWebhook.updatedAt)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Activity Log */}
              <div className="mt-6 border-t border-[var(--colors-border-light)] pt-4" data-id="element-5484">
                <h4 className={`text-sm ${styles.heading} mb-3`} data-id="element-5485">
                  Activity Log
                </h4>
                {infoModalWebhook.activity.length === 0 ? <p className={styles.subtext} data-id="element-5486">No activity recorded.</p> : <div className="space-y-0" data-id="element-5487">
                    {infoModalWebhook.activity.map((entry, idx) => <div key={idx} className="flex items-start gap-3 py-2.5 relative" data-id="element-5488">
                        {idx < infoModalWebhook.activity.length - 1 && <div className="absolute left-[7px] top-[22px] bottom-0 w-px bg-[var(--colors-border-light)]" data-id="element-5489" />}
                        <div className="w-[15px] h-[15px] rounded-full bg-[var(--colors-primary-100)] border-2 border-[var(--brand-primary)] flex-shrink-0 mt-0.5 relative z-10" data-id="element-5490" />
                        <div className="flex-1 min-w-0" data-id="element-5491">
                          <p className="text-sm text-[var(--colors-text-primary)] font-medium" data-id="element-5492">
                            {entry.action}
                          </p>
                          <p className="text-xs text-[var(--colors-text-tertiary)] mt-0.5" data-id="element-5493">
                            by {entry.user} · {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}